
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const includes = [
  "Your TapToTable Page built before activation",
  "Review your page and request changes",
  "Your hosted TapToTable Page",
  "Secure, Easy-to-Use TapToTable Dashboard",
  "Create and update Promotions, Events, and Featured Items anytime",
  "Update restaurant details, photos, links, and customer actions",
  "Customer activity and supported analytics",
  "Restaurant photo hosting + TapToTable hosting",
  "Ongoing support",
  "Cancel anytime",
];

const promotionIdeas = [
  "Limited-time specials",
  "Complimentary add-ons",
  "Loyalty rewards",
  "Seasonal promotions",
  "New menu launches",
  "Special occasion offers",
];

const eventIdeas = [
  "Live music",
  "Tastings",
  "Watch parties",
  "Holiday events",
  "Guest lists",
  "Ticketed events",
  "Reservations",
];

const featuredIdeas = [
  "Signature menu items",
  "Best-selling dishes",
  "Chef favorites",
  "Seasonal features",
  "Desserts and drinks",
  "Popular customer picks",
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-5xl mx-auto"
        >
          <div className="label-caps">Built Before You Activate</div>

          <h2 className="mt-3 font-display font-semibold text-2xl sm:text-3xl lg:text-[2.2rem] xl:text-4xl tracking-tight leading-tight lg:whitespace-nowrap">
            Turn Online Interest Into More Restaurant Visits
          </h2>

          <p className="mt-4 text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto">
            We build your TapToTable Page before you activate it. Review the
            complete customer experience, request any changes, and activate only
            when you&apos;re satisfied with your page.
          </p>
        </motion.div>

        <div className="mt-14 max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl p-[1px] bg-gradient-to-b from-[#00E5FF]/50 via-[#00E5FF]/10 to-transparent"
          >
            <div
              className="relative rounded-3xl bg-gradient-to-b from-[#1A1A1F] to-[#0B0B0E] p-8 md:p-10 overflow-hidden"
              data-testid="pricing-card"
            >
              <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#00E5FF]/15 blur-3xl pointer-events-none" />

              <div className="flex flex-wrap items-center gap-2">
                <span className="label-caps !text-[10px] !text-[#00E5FF]">
                  TapToTable
                </span>

                <span className="text-xs bg-[#00E5FF] text-black font-semibold px-2 py-0.5 rounded-full">
                  Built Before Activation
                </span>
              </div>

              <div className="mt-4">
                <div className="flex items-end gap-2">
                  <span className="font-display font-semibold text-5xl md:text-6xl tracking-tight">
                    $59
                  </span>
                  <span className="text-zinc-400 text-lg">/month</span>
                </div>

                <div className="mt-2 text-zinc-300 text-sm">
                  First restaurant location. Everything included. No contracts.
                  Cancel anytime.
                </div>

                <div className="mt-3 text-sm text-[#00E5FF] font-medium leading-relaxed">
                  At $59 per month, just a few additional orders or filled
                  tables could cover the cost.
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <div className="label-caps !text-[10px] !text-[#00E5FF]">
                  Additional Locations
                </div>

                <div className="mt-3 flex items-end gap-2">
                  <span className="font-display font-semibold text-3xl tracking-tight text-white">
                    $39
                  </span>
                  <span className="text-zinc-400 text-sm">
                    /month for each additional location
                  </span>
                </div>

                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  If the same owner has more than one restaurant location, the
                  first location is $59/month and each additional location is
                  $39/month.
                </p>

                <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                  Each location currently uses its own login and dashboard.
                </p>
              </div>

              <p className="mt-5 text-sm text-zinc-400 leading-relaxed">
                After activation, use your secure, easy-to-use TapToTable
                Dashboard to create and update Promotions, Events, and Featured
                Items, manage restaurant details, change photos, links, and
                customer actions, and monitor customer activity and supported
                analytics.
              </p>

              <div className="mt-7 rounded-2xl border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-5">
                <div className="label-caps !text-[10px] !text-[#00E5FF]">
                  What Can You Promote?
                </div>

                <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
                  TapToTable gives you three main campaign types so you can
                  promote what matters most right now.
                </p>

                <div className="mt-5 grid gap-5 lg:grid-cols-3">
                  <div>
                    <div className="text-sm font-semibold text-white">
                      Offers &amp; Promotions
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                      Give customers a reason to visit now with an incentive or
                      time-sensitive offer.
                    </p>
                    <ul className="mt-3 space-y-2">
                      {promotionIdeas.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-xs leading-relaxed text-zinc-400"
                        >
                          <span className="mt-0.5 text-[#00E5FF]">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-white">
                      Events
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                      Promote upcoming events and send customers to the next
                      step you want them to take.
                    </p>
                    <ul className="mt-3 space-y-2">
                      {eventIdeas.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-xs leading-relaxed text-zinc-400"
                        >
                          <span className="mt-0.5 text-[#00E5FF]">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-white">
                      Featured Items
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                      Spotlight a menu favorite without requiring a discount or
                      claim code.
                    </p>
                    <ul className="mt-3 space-y-2">
                      {featuredIdeas.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-xs leading-relaxed text-zinc-400"
                        >
                          <span className="mt-0.5 text-[#00E5FF]">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <p className="mt-5 text-xs leading-relaxed text-zinc-500">
                  Switch between Promotions, Events, and Featured Items anytime
                  from your easy-to-use TapToTable Dashboard.
                </p>
              </div>

              <ul className="mt-8 space-y-3">
                {includes.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-zinc-200"
                  >
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#00E5FF]/15 text-[#00E5FF] flex-shrink-0">
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className="mt-8 inline-flex w-full justify-center items-center gap-2 bg-[#00E5FF] text-black font-semibold px-6 py-3.5 rounded-full hover:bg-[#66FFFF] hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all"
                data-testid="pricing-cta"
              >
                Build My TapToTable Page
              </Link>

              <div className="mt-4 text-center text-xs text-zinc-500">
                Review it. Request changes. Activate only when you&apos;re ready.
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
